"use client";

import React, { useMemo } from "react";
import { Ticket } from "lucide-react";
import { TicketPopover } from "@/features/tickets/components/TicketPopover";
import { useTicket } from "@/lib/hooks/api/tickets";
import type { Ticket as TicketType } from "@/lib/types/database";

/**
 * Minimal shape SmartTicketDisplay reads from `ticketData`. Synthesized
 * relation rows match this shape without satisfying the full Tickets row
 * interface, so we accept this narrow type at the prop boundary.
 */
export interface SmartTicketDisplayData {
  id?: string;
  ticket_number?: number | string | null;
  title?: string | null;
}

interface SmartTicketDisplayProps {
  ticketId: string;
  ticketData?: SmartTicketDisplayData; // From relationship (if available)
  fallbackText?: string;
  showIcon?: boolean;
  className?: string;
  disabled?: boolean; // Disable popover when editing
  showPopover?: boolean; // Show popover on hover/click (default: true)
}

export function SmartTicketDisplay({
  ticketId,
  ticketData,
  fallbackText = "Unknown",
  showIcon = true,
  className = "",
  disabled = false,
  showPopover = true,
}: SmartTicketDisplayProps) {
  // Skip fetch when ticketData provided (e.g. from time_entries.tickets relation)
  const { data: ticket, isLoading } = useTicket(ticketId, { enabled: !ticketData });

  // Memoize ticket lookup to prevent unnecessary re-renders
  const ticketInfo = useMemo(() => {
    return ticketData || ticket;
  }, [ticketData, ticket]);

  // Memoize display name calculation - ONLY show ticket ID
  const displayName = useMemo(() => {
    if (!ticketInfo) return fallbackText;
    const ticketNumber = ticketInfo.ticket_number
      ? `WTC-${String(ticketInfo.ticket_number).padStart(6, "0")}`
      : "Unknown";
    return ticketNumber;
  }, [ticketInfo, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!ticketInfo && isLoading && !ticketData) {
    return <span className={`text-gray-400 ${className}`}>Loading...</span>;
  }

  if (!ticketInfo) {
    return <span className={`text-gray-400 ${className}`}>{fallbackText}</span>;
  }

  // If disabled, just show the text without popover
  if (disabled) {
    return (
      <div className={`flex items-center gap-2 rounded px-1 ${className}`}>
        {showIcon && <Ticket className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground truncate">{displayName}</span>
      </div>
    );
  }

  // Render without popover if showPopover is false
  if (!showPopover) {
    return (
      <div
        className={`flex items-center gap-2 cursor-pointer hover:bg-card-50 rounded px-1 ${className}`}
      >
        {showIcon && <Ticket className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground hover:text-blue-600 truncate">
          {displayName}
        </span>
      </div>
    );
  }

  return (
    <TicketPopover ticketId={ticketId}>
      <div
        className={`flex items-center gap-2 cursor-pointer hover:bg-card-50 rounded px-1 ${className}`}
      >
        {showIcon && <Ticket className="h-4 w-4 text-gray-400" />}
        <span className="text-sm text-card-foreground hover:text-blue-600 truncate">
          {displayName}
        </span>
      </div>
    </TicketPopover>
  );
}

interface SmartTicketChipProps {
  ticketId: string;
  ticketData?: TicketType;
  fallbackText?: string;
  size?: "sm" | "md";
  showPopover?: boolean; // Show popover on hover/click (default: true)
}

export function SmartTicketChip({
  ticketId,
  ticketData,
  fallbackText = "Unknown",
  size = "sm",
  showPopover = true,
}: SmartTicketChipProps) {
  const { data: ticket, isLoading } = useTicket(ticketId, { enabled: !ticketData });

  // Memoize ticket lookup to prevent unnecessary re-renders
  const ticketInfo = useMemo(() => {
    return ticketData || ticket;
  }, [ticketData, ticket]);

  // Memoize display name calculation - ONLY show ticket ID
  const displayName = useMemo(() => {
    if (!ticketInfo) return fallbackText;
    const ticketNumber = ticketInfo.ticket_number
      ? `WTC-${String(ticketInfo.ticket_number).padStart(6, "0")}`
      : "Unknown";
    return ticketNumber;
  }, [ticketInfo, fallbackText]);

  // Show loading state if we're fetching and don't have data
  if (!ticketInfo && isLoading && !ticketData) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        Loading...
      </span>
    );
  }

  if (!ticketInfo) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-600 dark:text-gray-400"
        }
      >
        {fallbackText}
      </span>
    );
  }

  // Render without popover if showPopover is false
  if (!showPopover) {
    return (
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-pointer hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:hover:bg-purple-900/50"
        }
      >
        {displayName}
      </span>
    );
  }

  return (
    <TicketPopover ticketId={ticketId}>
      <span
        className={
          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 cursor-pointer hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:hover:bg-purple-900/50"
        }
      >
        {displayName}
      </span>
    </TicketPopover>
  );
}
